import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Button, Loader, Message, Grid, Icon, Dropdown, Segment, Header, Table, Label, Dimmer } from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import MetricCard from '../../components/Dashboard/MetricCard';
import { campaignService } from '../../services/campaigns/campaignService';
import { CampaignResponseDto, TimelineMetricsDto, BonificationMetricsDto, TeamComparisonDto, TopPerformersDto, AllPerformersDto, CampaignSummaryDto, CampaignExtraResultsDto } from '../../types/campaigns.types';
import { TimelineChart } from '../../components/charts/TimelineChart';
import { TeamComparisonChart } from '../../components/charts/TeamComparisonChart';
import RankingTable from '../../components/Campaigns/RankingTable';
import CampaignEstimatesDisclaimer from '../../components/Campaigns/CampaignEstimatesDisclaimer';
import ContractListModal from '../../components/Dashboard/ContractListModal';
import GlassmorphismTheme from '../../styles/glassmorphism';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import { getCampaignDateRange, formatWeeklyWindowRange, formatCampaignPeriodLabel } from '../../constants/campaignScoringDefaults';
import { CampaignRevenueContractBucket } from '../../types/campaigns.types';
import { formatDateTime } from '../../lib/format-utils';

const normalizeSellerName = (name?: string | null) => (name ?? '').trim().toUpperCase();

const CampaignDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const { hasRole } = useAuth();

  const isAdmin = hasRole('Admin');
  const isTeamLeader = hasRole('TeamLeader');

  // Estados principais
  const [campaigns, setCampaigns] = useState<CampaignResponseDto[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<CampaignResponseDto | null>(null);
  const [timelineMetrics, setTimelineMetrics] = useState<TimelineMetricsDto | null>(null);
  const [bonificationMetrics, setBonificationMetrics] = useState<BonificationMetricsDto | null>(null);
  const [teamComparison, setTeamComparison] = useState<TeamComparisonDto | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformersDto | null>(null);
  const [allPerformers, setAllPerformers] = useState<AllPerformersDto | null>(null);
  const [campaignSummary, setCampaignSummary] = useState<CampaignSummaryDto | null>(null);
  const [extraResults, setExtraResults] = useState<CampaignExtraResultsDto | null>(null);
  const [ultimaAtualizacaoDados, setUltimaAtualizacaoDados] = useState<string | null>(null);
  const [dashboardFromCache, setDashboardFromCache] = useState(false);

  // Estados de UI
  const [campaignsReady, setCampaignsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Carregando dashboard...');
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDashboardBusy = loading || recalculating;

  // Estados de filtros de data
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);

  const [showContractModal, setShowContractModal] = useState(false);
  const [contractModalLabel, setContractModalLabel] = useState('');
  const [contractModalBucket, setContractModalBucket] = useState<CampaignRevenueContractBucket | null>(null);
  const [contractModalMode, setContractModalMode] = useState<'revenue' | 'points' | 'weeklyBonus'>('revenue');
  const [weeklyBonusModalContext, setWeeklyBonusModalContext] = useState<{
    windowIndex: number;
    userId: string;
    userName: string;
    semanaLabel?: string;
    dataInicio: string;
    dataFim: string;
    horaInicio?: string;
    horaFim?: string;
  } | null>(null);

  const loadedDashboardIdRef = useRef<string | null>(null);
  const previousCampaignIdRef = useRef<string | null>(null);
  const loadGenerationRef = useRef(0);
  const dashboardLoadInFlightRef = useRef<string | null>(null);
  const reloadAttemptedRef = useRef(false);

  const clearDashboardMetrics = useCallback(() => {
    setCampaignSummary(null);
    setTimelineMetrics(null);
    setBonificationMetrics(null);
    setTeamComparison(null);
    setTopPerformers(null);
    setAllPerformers(null);
    setExtraResults(null);
    setUltimaAtualizacaoDados(null);
    setDashboardFromCache(false);
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      setCampaignsReady(false);
      const data = await campaignService.getCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      console.error('Erro ao carregar campanhas:', err);
      showError('Erro ao carregar lista de campanhas');
      setCampaigns([]);
    } finally {
      setCampaignsReady(true);
    }
  }, [showError]);

  const loadDashboardData = useCallback(async (
    campaignId: string,
    dateOverride?: { start: string; end: string },
    options?: { skipRecalcWait?: boolean; skipClear?: boolean },
  ) => {
    const generation = ++loadGenerationRef.current;
    const isStale = () => generation !== loadGenerationRef.current;

    try {
      setLoading(true);
      setError(null);
      if (!options?.skipClear) {
        clearDashboardMetrics();
      }
      setLoadingMessage('Carregando campanha...');

      let campaignData = await campaignService.getCampaignById(campaignId);
      if (isStale()) return;

      setCampaign(campaignData);
      setSelectedCampaignId(campaignId);

      let didWaitRecalc = false;
      if (!options?.skipRecalcWait && campaignData.eligibilityRules?.recalculando) {
        didWaitRecalc = true;
        setLoadingMessage('Recalculando métricas da campanha...');
        await campaignService.waitForRecalculationComplete(campaignId);
        if (isStale()) return;
        campaignData = await campaignService.getCampaignById(campaignId);
        if (isStale()) return;
        setCampaign(campaignData);
      }

      const { start: defaultStart, end: defaultEnd } = getCampaignDateRange(campaignData);
      const campaignChanged = previousCampaignIdRef.current !== campaignId;

      let effectiveStart: string;
      let effectiveEnd: string;

      if (dateOverride) {
        effectiveStart = dateOverride.start;
        effectiveEnd = dateOverride.end;
      } else if (campaignChanged || !startDate || !endDate) {
        effectiveStart = defaultStart;
        effectiveEnd = defaultEnd;
        setStartDate(defaultStart);
        setEndDate(defaultEnd);
      } else {
        effectiveStart = startDate;
        effectiveEnd = endDate;
      }

      previousCampaignIdRef.current = campaignId;

      const fetchDashboardWithRetry = async () => {
        const maxAttempts = 3;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            return await campaignService.getCampaignAnalyticsDashboard(
              campaignId,
              effectiveStart,
              effectiveEnd,
            );
          } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || '';
            const isRecalcRace = message.toLowerCase().includes('recálculo');
            if (attempt < maxAttempts - 1 && isRecalcRace) {
              setLoadingMessage('Finalizando recálculo das métricas...');
              await new Promise((resolve) => setTimeout(resolve, 1500));
              continue;
            }
            throw err;
          }
        }
        throw new Error('Não foi possível carregar as métricas da campanha');
      };

      let dashboardPayload: Awaited<ReturnType<typeof campaignService.getCampaignAnalyticsDashboard>> | null = null;

      try {
        setLoadingMessage('Montando métricas do dashboard...');
        dashboardPayload = await fetchDashboardWithRetry();
        if (isStale()) return;

        setCampaignSummary(dashboardPayload.summary);
        setTimelineMetrics(dashboardPayload.timeline);
        setBonificationMetrics(dashboardPayload.bonifications);
        setTeamComparison(dashboardPayload.teamComparison);
        setTopPerformers(dashboardPayload.topPerformers);
        setUltimaAtualizacaoDados(
          dashboardPayload.ultimaAtualizacaoDados
            ?? dashboardPayload.computedAt
            ?? campaignData.ultimaAtualizacaoDados
            ?? campaignData.eligibilityRules?.ultimoRecalculoConcluidoEm
            ?? null,
        );
        setDashboardFromCache(dashboardPayload.fromCache);

        let performersPayload = dashboardPayload.allPerformers ?? null;
        if (!performersPayload || !Array.isArray(performersPayload.performers)) {
          setLoadingMessage('Montando ranking completo...');
          performersPayload = await campaignService.getAllPerformers(
            campaignId,
            effectiveStart,
            effectiveEnd,
          );
          if (isStale()) return;
        }
        setAllPerformers(performersPayload);
      } catch (dashboardErr) {
        console.error('Erro ao carregar dashboard consolidado:', dashboardErr);
        throw new Error('Não foi possível carregar as métricas da campanha');
      }

      const hasExtraCampaigns = Boolean(
        campaignData.bonificationRules?.campanhaTop1Ativa
        || campaignData.bonificationRules?.campanhaSemanalAtiva,
      );

      if (hasExtraCampaigns) {
        if (dashboardPayload?.extraResults) {
          setExtraResults(dashboardPayload.extraResults);
        } else {
          try {
            setLoadingMessage('Carregando campanhas extras...');
            const extra = await campaignService.getExtraResults(campaignId);
            if (isStale()) return;
            setExtraResults(extra);
          } catch {
            if (isStale()) return;
            setExtraResults(null);
          }
        }
      } else {
        setExtraResults(null);
      }

      if (
        didWaitRecalc
        && !reloadAttemptedRef.current
        && dashboardPayload
        && isDashboardEffectivelyEmpty(dashboardPayload.summary, hasExtraCampaigns, dashboardPayload.extraResults ?? null)
      ) {
        reloadAttemptedRef.current = true;
        window.location.reload();
        return;
      }

      loadedDashboardIdRef.current = campaignId;
    } catch (err: any) {
      if (isStale()) return;
      console.error('Erro ao carregar dados do dashboard:', err);
      const message = err.response?.data?.message || err.message || 'Erro ao carregar dados do dashboard';
      setError(message);
      loadedDashboardIdRef.current = campaignId;
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [clearDashboardMetrics, startDate, endDate]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (!campaignsReady) return;

    if (campaigns.length === 0) {
      setLoading(false);
      return;
    }

    const campaignParam = searchParams.get('campaign');
    const validParam = campaignParam && campaigns.some((c) => c.id === campaignParam);
    const targetId = validParam
      ? campaignParam!
      : (campaigns.find((c) => c.ativa) || campaigns[0]).id;

    if (!validParam) {
      setSearchParams({ campaign: targetId }, { replace: true });
      return;
    }

    if (loadedDashboardIdRef.current === targetId) {
      return;
    }

    if (dashboardLoadInFlightRef.current === targetId) {
      return;
    }

    dashboardLoadInFlightRef.current = targetId;
    setLoading(true);
    void loadDashboardData(targetId).finally(() => {
      if (dashboardLoadInFlightRef.current === targetId) {
        dashboardLoadInFlightRef.current = null;
      }
    });
    // loadDashboardData omitido de propósito — evita re-fetch ao atualizar filtros de data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsReady, campaigns, searchParams]);

  const handleRecalculateMetrics = async () => {
    if (!selectedCampaignId) return;

    try {
      setRecalculating(true);
      setLoading(true);
      clearDashboardMetrics();
      setLoadingMessage('Iniciando recálculo da campanha...');
      await campaignService.recalculateAllMetrics(selectedCampaignId);
      setLoadingMessage('Recalculando métricas da campanha...');
      await campaignService.waitForRecalculationComplete(selectedCampaignId);
      setRecalculating(false);
      loadedDashboardIdRef.current = null;
      await loadDashboardData(selectedCampaignId, undefined, {
        skipRecalcWait: true,
        skipClear: true,
      });
      showSuccess('Métricas atualizadas com sucesso!');
    } catch (err: any) {
      console.error('Erro ao recalcular métricas:', err);
      showError(err.message || 'Erro ao atualizar métricas');
    } finally {
      setRecalculating(false);
      setLoading(false);
    }
  };

  const handleCampaignChange = (campaignId: string) => {
    const selected = campaigns.find((c) => c.id === campaignId);
    if (selected) {
      const { start, end } = getCampaignDateRange(selected);
      setStartDate(start);
      setEndDate(end);
    }
    loadedDashboardIdRef.current = null;
    previousCampaignIdRef.current = null;
    clearDashboardMetrics();
    setCampaign(null);
    setLoading(true);
    setSearchParams({ campaign: campaignId });
  };

  const handleRevenueCardClick = (bucket: CampaignRevenueContractBucket, label: string) => {
    if (!selectedCampaignId) return;
    setWeeklyBonusModalContext(null);
    setContractModalMode('revenue');
    setContractModalBucket(bucket);
    setContractModalLabel(label);
    setShowContractModal(true);
  };

  const handlePointsCardClick = () => {
    if (!selectedCampaignId) return;
    setWeeklyBonusModalContext(null);
    setContractModalMode('points');
    setContractModalBucket(null);
    setContractModalLabel('Pontos Totais');
    setShowContractModal(true);
  };

  const handleWeeklyBonusRowClick = (
    windowIndex: number,
    consultant: { userId: string; userName: string },
    semana: { label?: string; dataInicio: string; dataFim: string; horaInicio?: string; horaFim?: string },
  ) => {
    if (!selectedCampaignId) return;
    const semanaLabel = semana.label || `Semana ${windowIndex + 1}`;
    const intervalo = formatWeeklyWindowRange(
      semana.dataInicio,
      semana.dataFim,
      semana.horaInicio,
      semana.horaFim,
    );
    setWeeklyBonusModalContext({
      windowIndex,
      userId: consultant.userId,
      userName: normalizeSellerName(consultant.userName),
      semanaLabel,
      dataInicio: semana.dataInicio,
      dataFim: semana.dataFim,
      horaInicio: semana.horaInicio,
      horaFim: semana.horaFim,
    });
    setContractModalMode('weeklyBonus');
    setContractModalBucket(null);
    setContractModalLabel(`Bonificação semanal — ${normalizeSellerName(consultant.userName)} — ${semanaLabel} (${intervalo})`);
    setShowContractModal(true);
  };

  const handleCloseContractModal = () => {
    setShowContractModal(false);
    setContractModalBucket(null);
    setContractModalLabel('');
    setContractModalMode('revenue');
    setWeeklyBonusModalContext(null);
  };

  // Calcular projeções e análises
  const projections = useMemo(() => {
    if (!campaign || !timelineMetrics || !campaignSummary) return null;

    const receitaAprovada = campaignSummary.receitaElegivel ?? 0;
    const receitaEmAprovacao = campaignSummary.receitaEmAprovacao ?? 0;
    const incluirNaMeta = campaignSummary.incluirEmAprovacaoNaMeta ?? false;
    const receitaAcumulada = incluirNaMeta ? receitaAprovada + receitaEmAprovacao : receitaAprovada;
    const meta = campaign.metaEmpresa;
    const faltam = Math.max(0, meta - receitaAcumulada);
    const progresso = campaignSummary.progresso;

    const hoje = new Date();
    const { start: vigenciaInicio, end: vigenciaFim } = getCampaignDateRange(campaign);
    const inicioMes = vigenciaInicio ? new Date(`${vigenciaInicio}T12:00:00`) : hoje;
    const fimMes = vigenciaFim ? new Date(`${vigenciaFim}T12:00:00`) : hoje;
    const diasNoMes = Math.max(
      1,
      Math.round((fimMes.getTime() - inicioMes.getTime()) / (1000 * 60 * 60 * 24)) + 1,
    );
    const diaAtual = hoje >= inicioMes && hoje <= fimMes
      ? Math.round((hoje.getTime() - inicioMes.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : diasNoMes;
    const diasDecorridos = Math.max(1, diaAtual);
    const diasRestantes = Math.max(0, diasNoMes - diaAtual);

    const ritmoAtual = diasDecorridos > 0 ? receitaAcumulada / diasDecorridos : 0;
    const ritmoNecessario = diasRestantes > 0 ? faltam / diasRestantes : faltam;
    const projecaoFinal = diasNoMes * ritmoAtual;
    const atingiraMeta = projecaoFinal >= meta;

    return {
      receitaAcumulada,
      receitaAprovada,
      receitaEmAprovacao,
      incluirNaMeta,
      meta,
      faltam,
      progresso,
      diasDecorridos,
      diasRestantes,
      diasNoMes,
      ritmoAtual,
      ritmoNecessario,
      projecaoFinal,
      atingiraMeta
    };
  }, [campaign, timelineMetrics, campaignSummary]);

  const campaignOptions = campaigns.map(c => ({
    key: c.id,
    text: `${c.nome} — ${formatCampaignPeriodLabel(c)}${c.ativa ? ' ✓' : ''}`,
    value: c.id,
  }));

  const metricsReady = campaignSummary !== null && !loading;
  const showInitialLoader = !campaignsReady || (loading && campaignSummary === null && campaigns.length > 0);

  if (isTeamLeader && !isAdmin) {
    return <Navigate to="/campaigns/my-performance" replace />;
  }

  if (showInitialLoader) {
    return (
      <GlassPageWrapper title="Dashboard de Campanhas" icon="bullhorn" fillViewport>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <Loader active size="large">{loadingMessage}</Loader>
        </div>
      </GlassPageWrapper>
    );
  }

  if (campaigns.length === 0) {
    return (
      <GlassPageWrapper title="Dashboard de Campanhas" icon="bullhorn" fillViewport>
        <Message info>
          <Message.Header>Nenhuma campanha encontrada</Message.Header>
          <p>Cadastre uma campanha em Gestão para visualizar o dashboard.</p>
          {isAdmin && (
            <Button primary onClick={() => navigate('/campaigns/manage')} style={{ marginTop: '12px' }}>
              <Icon name="settings" /> Ir para Gestão
            </Button>
          )}
        </Message>
      </GlassPageWrapper>
    );
  }

  if (error && !campaign) {
    return (
      <GlassPageWrapper title="Dashboard de Campanhas" icon="bullhorn" fillViewport>
        <Message error>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Dashboard de Campanhas" icon="bullhorn" fillViewport>
      <style>{`
        .campaign-dashboard-dropdown.ui.dropdown .menu {
          z-index: 2001 !important;
          max-height: 320px;
          overflow-y: auto;
        }
        .campaign-metrics-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 1rem;
          align-items: stretch;
        }
        @media (max-width: 1400px) {
          .campaign-metrics-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 900px) {
          .campaign-metrics-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 520px) {
          .campaign-metrics-grid {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      `}</style>
      <div style={{ position: 'relative' }}>
          <Dimmer active={isDashboardBusy} page>
            <Loader size="large">{loadingMessage}</Loader>
          </Dimmer>

          <p style={{ color: GlassmorphismTheme.colors.solid.textLight, margin: '0 0 12px' }}>
            {ultimaAtualizacaoDados && (
              <span style={{ display: 'block', fontSize: '0.9rem' }}>
                <Icon name="history" />
                Última atualização dos dados: {formatDateTime(ultimaAtualizacaoDados)}
                {dashboardFromCache && (
                  <Label size="mini" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                    cache
                  </Label>
                )}
              </span>
            )}
          </p>

          <CampaignEstimatesDisclaimer />

          {/* Módulos desativados temporariamente (Admin): Comparar Campanhas e Importar Excel
          {isAdmin && (
            <div style={{
              marginBottom: GlassmorphismTheme.spacing.md,
              display: 'flex',
              flexWrap: 'wrap',
              gap: GlassmorphismTheme.spacing.sm,
            }}>
              <Button
                onClick={() => navigate('/campaigns/compare')}
                style={GlassmorphismTheme.components.buttonGlass}
              >
                <Icon name="exchange" /> Comparar Campanhas
              </Button>
              <Button
                onClick={() => navigate('/contracts/excel-import')}
                style={GlassmorphismTheme.components.buttonGlass}
              >
                <Icon name="file excel" /> Importar Excel
              </Button>
            </div>
          )}
          */}

          <>
              {/* Filtros */}
              <div style={{
                ...GlassmorphismTheme.components.glassCard,
                padding: GlassmorphismTheme.spacing.md,
                marginBottom: GlassmorphismTheme.spacing.lg,
                display: 'flex',
                gap: GlassmorphismTheme.spacing.md,
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                position: 'relative',
                zIndex: campaignMenuOpen ? 200 : 10,
                overflow: 'visible',
              }}>
                {/* Botão Gerenciar Campanhas - ESQUERDA */}
                {isAdmin && (
                  <Button 
                    onClick={() => navigate('/campaigns/manage')} 
                    style={{
                      ...GlassmorphismTheme.components.buttonGlass,
                    }}
                  >
                    <Icon name="settings" /> Gerenciar Campanhas
                  </Button>
                )}

                {/* Dropdown Campanha */}
                <div style={{
                  flex: 1,
                  minWidth: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  zIndex: campaignMenuOpen ? 201 : 1,
                }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    height: '20px',
                    lineHeight: '20px'
                  }}>
                    <Icon name="filter" style={{ marginRight: '6px' }} />
                    Selecione a Campanha
                  </label>
                  <Dropdown
                    className="campaign-dashboard-dropdown"
                    selection
                    search
                    disabled={isDashboardBusy}
                    options={campaignOptions}
                    value={selectedCampaignId || ''}
                    onChange={(_, data) => handleCampaignChange(data.value as string)}
                    onOpen={() => setCampaignMenuOpen(true)}
                    onClose={() => setCampaignMenuOpen(false)}
                    placeholder="Selecione uma campanha"
                    style={{
                      minWidth: '100%',
                      background: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.7)',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      fontSize: '0.95rem',
                      color: GlassmorphismTheme.colors.solid.text
                    }}
                  />
                </div>

                {/* Botões de Ação */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {isAdmin && (
                  <Button
                    onClick={handleRecalculateMetrics}
                    loading={recalculating}
                    disabled={isDashboardBusy || !selectedCampaignId}
                    style={{
                      ...GlassmorphismTheme.components.buttonGlass,
                      opacity: (isDashboardBusy || !selectedCampaignId) ? 0.5 : 1,
                    }}
                  >
                    <Icon name="refresh" /> Atualizar
                  </Button>
                  )}
                </div>
              </div>

              {!campaign ? (
                <Message info>
                  <Message.Header>Nenhuma campanha selecionada</Message.Header>
                  <p>Selecione uma campanha no dropdown acima para visualizar as métricas.</p>
                </Message>
              ) : !metricsReady ? (
                <Message info>
                  <Message.Header>Preparando métricas</Message.Header>
                  <p>Aguarde enquanto os dados da campanha são calculados.</p>
                </Message>
              ) : (
                <>
                  {/* Seção: Métricas Principais */}
                  <div style={{ marginBottom: GlassmorphismTheme.spacing.xl }}>
                    <Segment style={GlassmorphismTheme.components.glassCard}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: GlassmorphismTheme.spacing.md,
                        paddingBottom: GlassmorphismTheme.spacing.sm,
                        borderBottom: '2px solid rgba(255, 255, 255, 0.15)'
                      }}>
                        <Icon name="chart bar" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
                        <h3 style={{
                          margin: 0,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }}>
                          Visão Geral
                        </h3>
                      </div>

                      <div className="campaign-metrics-grid">
                          <MetricCard
                            title="Meta Empresa"
                            value={`R$ ${(campaign.metaEmpresa ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon="target"
                            color="#3498db"
                            tooltip="Meta total da empresa para a campanha (R$)"
                          />
                          <MetricCard
                            title="Receita Aprovada"
                            value={`R$ ${(campaignSummary?.receitaElegivel ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon="dollar"
                            color="#2ecc71"
                            tooltip="Receita elegível pela meta (ativação VTME ou funil histórico). Clique para ver os contratos."
                            clickable
                            onClick={() => handleRevenueCardClick('receitaAprovada', 'Receita Aprovada')}
                          />
                          <MetricCard
                            title="Em Aprovação"
                            value={`R$ ${(campaignSummary?.receitaEmAprovacao ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon="clock outline"
                            color="#9b59b6"
                            tooltip="Contratos sem ativação VTME em status configurados como Em Aprovação. Clique para ver os contratos."
                            clickable
                            onClick={() => handleRevenueCardClick('emAprovacao', 'Em Aprovação')}
                          />
                          <MetricCard
                            title="Receita Total"
                            value={`R$ ${(campaignSummary?.receitaTotal ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon="chart line"
                            color="#1abc9c"
                            tooltip="Soma da receita de contratos criados no período que já passaram por VTME Criado (sem linhas de suspensão)."
                          />
                          <MetricCard
                            title="Progresso"
                            value={`${projections?.progresso?.toFixed(1) ?? 0}%`}
                            icon="percent"
                            color={projections && projections.progresso >= 100 ? '#2ecc71' : projections && projections.progresso >= 70 ? '#3498db' : '#f39c12'}
                            tooltip={
                              campaignSummary?.incluirEmAprovacaoNaMeta
                                ? 'Percentual da meta com receita aprovada + em aprovação'
                                : 'Percentual da meta com receita aprovada (em aprovação exibida separadamente)'
                            }
                          />
                          <MetricCard
                            title="Equipes Participantes"
                            value={campaignSummary?.equipesParticipantes || 0}
                            icon="users"
                            color="#9b59b6"
                            tooltip="Número de equipes que têm contratos na campanha"
                          />
                          {campaignSummary?.usaSistemaPontos && (
                            <>
                              <MetricCard
                                title="Pontos Totais"
                                value={campaignSummary.pontosTotais?.toFixed(1) || '0.0'}
                                icon="star"
                                color="#e67e22"
                                tooltip="Soma de todos os pontos acumulados pelos vendedores. Clique para ver as linhas consideradas na pontuação."
                                clickable
                                onClick={handlePointsCardClick}
                              />
                              <MetricCard
                                title="Bonificação Total"
                                value={`R$ ${(campaignSummary.bonificacaoTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                icon="money bill alternate"
                                color="#27ae60"
                                tooltip="Total de bonificações a serem pagas aos vendedores"
                              />
                            </>
                          )}
                      </div>
                    </Segment>
                  </div>

                  {/* Campanhas extras — TOP1 / Semanal (Admin) */}
                  {isAdmin && extraResults && (
                    <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: GlassmorphismTheme.spacing.md,
                        paddingBottom: GlassmorphismTheme.spacing.sm,
                        borderBottom: '2px solid rgba(255, 255, 255, 0.15)',
                      }}>
                        <Icon name="trophy" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
                        <h3 style={{
                          margin: 0,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                        }}>
                          Campanhas Extras — TOP 1 e Semanal
                        </h3>
                      </div>

                      <Segment style={GlassmorphismTheme.components.glassCard}>
                        {extraResults.top1 ? (
                          <div style={{ marginBottom: '1.5rem' }}>
                            <Header as="h4" style={{ color: '#2c3e50' }}>
                              Vencedor TOP 1 do mês
                            </Header>
                            <Table celled compact>
                              <Table.Body>
                                <Table.Row>
                                  <Table.Cell><strong>Consultor</strong></Table.Cell>
                                  <Table.Cell>{normalizeSellerName(extraResults.top1.userName)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                  <Table.Cell><strong>Receita Aprovada</strong></Table.Cell>
                                  <Table.Cell>
                                    R$ {(extraResults.top1.receitaAprovada ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                  <Table.Cell><strong>Premiação</strong></Table.Cell>
                                  <Table.Cell>
                                    {extraResults.top1.tipoPremio === 'Premio' ||
                                    extraResults.top1.descricaoPremio ? (
                                      extraResults.top1.descricaoPremio
                                    ) : (
                                      <>R$ {extraResults.top1.premiacao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                                {extraResults.top1.empateCount > 1 && (
                                  <Table.Row>
                                    <Table.Cell><strong>Empate</strong></Table.Cell>
                                    <Table.Cell>
                                      <Label color="orange">{extraResults.top1.empateCount} consultores empatados</Label>
                                    </Table.Cell>
                                  </Table.Row>
                                )}
                              </Table.Body>
                            </Table>
                          </div>
                        ) : (
                          <Message info compact content="Nenhum vencedor TOP 1 calculado ainda." />
                        )}

                        {extraResults.semanais?.length > 0 ? (
                          <div>
                            <Header as="h4" style={{ color: '#2c3e50' }}>
                              Premiações semanais
                            </Header>
                            {extraResults.semanais.map((semana, idx) => {
                              const showPremioFixoColumn = (semana.valorPremiacaoFixo ?? 0) > 0;
                              return (
                              <Segment key={idx} style={{ marginBottom: '1rem' }}>
                                <Header as="h5">
                                  {semana.label || `Semana ${idx + 1}`}
                                  <span style={{ fontWeight: 400, fontSize: '0.9em', marginLeft: '8px', color: '#666' }}>
                                    ({formatWeeklyWindowRange(semana.dataInicio, semana.dataFim, semana.horaInicio, semana.horaFim)})
                                  </span>
                                </Header>
                                <div style={{ marginBottom: '8px', color: '#2c3e50' }}>
                                  Premiação: R$ {semana.valorPremiacao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                                {semana.consultores && semana.consultores.length > 0 ? (
                                  <Table celled compact size="small">
                                    <Table.Header>
                                      <Table.Row>
                                        <Table.HeaderCell>Consultor</Table.HeaderCell>
                                        <Table.HeaderCell>Bonif. linhas</Table.HeaderCell>
                                        {showPremioFixoColumn && (
                                          <Table.HeaderCell>Prêmio fixo</Table.HeaderCell>
                                        )}
                                        <Table.HeaderCell>Total</Table.HeaderCell>
                                      </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                      {semana.consultores.map((c) => (
                                        <Table.Row
                                          key={c.userId}
                                          style={{ cursor: 'pointer' }}
                                          title="Ver contratos da bonificação"
                                          onClick={() => handleWeeklyBonusRowClick(idx, c, semana)}
                                        >
                                          <Table.Cell>{normalizeSellerName(c.userName)}</Table.Cell>
                                          <Table.Cell>
                                            R$ {c.bonificacaoLinhas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                          </Table.Cell>
                                          {showPremioFixoColumn && (
                                            <Table.Cell>
                                              R$ {c.premioFixo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                            </Table.Cell>
                                          )}
                                          <Table.Cell>
                                            R$ {c.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                          </Table.Cell>
                                        </Table.Row>
                                      ))}
                                    </Table.Body>
                                  </Table>
                                ) : semana.vencedores?.length > 0 ? (
                                  <Table celled compact size="small">
                                    <Table.Header>
                                      <Table.Row>
                                        <Table.HeaderCell>Consultor</Table.HeaderCell>
                                        <Table.HeaderCell>Pontos</Table.HeaderCell>
                                        <Table.HeaderCell>Premiação</Table.HeaderCell>
                                      </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                      {semana.vencedores.map((v) => (
                                        <Table.Row key={v.userId}>
                                          <Table.Cell>{normalizeSellerName(v.userName)}</Table.Cell>
                                          <Table.Cell>{v.pontos.toFixed(1)}</Table.Cell>
                                          <Table.Cell>
                                            R$ {v.premiacao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                          </Table.Cell>
                                        </Table.Row>
                                      ))}
                                    </Table.Body>
                                  </Table>
                                ) : (
                                  <Message info compact content="Nenhum vencedor nesta semana." />
                                )}
                              </Segment>
                            );
                            })}
                          </div>
                        ) : (
                          <Message info compact content="Nenhuma premiação semanal configurada ou calculada." />
                        )}
                      </Segment>
                    </div>
                  )}

                  {/* Performance dos Vendedores - Ranking */}
                  {topPerformers && (
                    <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
                      <RankingTable
                        top5Melhor={topPerformers.top5Melhor ?? []}
                        top5Pior={topPerformers.top5Pior ?? []}
                        allPerformers={allPerformers?.performers}
                        usaSistemaPontos={
                          topPerformers.usaSistemaPontos
                          ?? allPerformers?.usaSistemaPontos
                          ?? campaignSummary?.usaSistemaPontos
                          ?? false
                        }
                        campaignId={selectedCampaignId ?? undefined}
                        campaignStartDate={startDate}
                        campaignEndDate={endDate}
                      />
                    </div>
                  )}

                  {/* DUMMY GRID START - TO BE REMOVED */}
                  {false && (
                    <div>
                      <Grid stackable columns={2}>
                      </Grid>
                    </div>
                  )}

                  {/* Gráficos */}
                  <Grid
                    stackable
                    columns={2}
                    style={{
                      marginTop: GlassmorphismTheme.spacing.lg,
                      marginBottom: GlassmorphismTheme.spacing.xl,
                    }}
                  >
                    <Grid.Column>
                      <Segment style={{ ...GlassmorphismTheme.components.glassCard, height: '100%' }}>
                        {timelineMetrics?.dataPoints && timelineMetrics.dataPoints.length > 0 && (
                          <TimelineChart
                            data={timelineMetrics.dataPoints}
                            title={`Timeline - ${timelineMetrics.campaignNome}`}
                            usaReceita={timelineMetrics.usaReceita}
                          />
                        )}
                      </Segment>
                    </Grid.Column>
                    <Grid.Column>
                      <Segment style={{ ...GlassmorphismTheme.components.glassCard, height: '100%' }}>
                        {teamComparison && (
                          <TeamComparisonChart
                            data={teamComparison.teams}
                            title={`Comparação de Equipes - ${teamComparison.campaignNome}`}
                          />
                        )}
                      </Segment>
                    </Grid.Column>
                  </Grid>
                </>
              )}
          </>
      </div>

      <ContractListModal
        isOpen={showContractModal}
        onClose={handleCloseContractModal}
        statuses={null}
        statusLabel={contractModalLabel}
        mode={
          contractModalMode === 'points'
            ? 'campaignPoints'
            : contractModalMode === 'weeklyBonus'
            ? 'campaignWeeklyBonus'
            : 'campaign'
        }
        campaignId={selectedCampaignId ?? undefined}
        campaign={campaign ?? undefined}
        campaignBucket={contractModalMode === 'revenue' ? contractModalBucket ?? undefined : undefined}
        campaignStartDate={startDate}
        campaignEndDate={endDate}
        weeklyBonusWindowIndex={weeklyBonusModalContext?.windowIndex}
        weeklyBonusUserId={weeklyBonusModalContext?.userId}
      />
    </GlassPageWrapper>
  );
};

export default CampaignDashboardPage;

function isDashboardEffectivelyEmpty(
  summary: CampaignSummaryDto | null | undefined,
  hasExtraCampaigns: boolean,
  extraResults: CampaignExtraResultsDto | null,
): boolean {
  if (!summary) return true;

  const noMainMetrics =
    summary.receitaElegivel === 0
    && (summary.receitaEmAprovacao ?? 0) === 0
    && (summary.pontosTotais ?? 0) === 0
    && summary.receitaTotal === 0;

  if (!noMainMetrics) return false;

  if (!hasExtraCampaigns) return true;

  const hasTop1 = Boolean(extraResults?.top1);
  const hasSemanais = (extraResults?.semanais?.length ?? 0) > 0;
  return !hasTop1 && !hasSemanais;
}
